if (!customElements.get('purecarnivore-reviews')) {
  customElements.define(
    'purecarnivore-reviews',
    class PureCarnivoreReviews extends HTMLElement {
      constructor() {
        super();
        this.currentPage = 1;
        this.loadingAll = false;
        this.refreshTimer = null;
        this.observer = null;
      }

      connectedCallback() {
        this.pagination = this.querySelector('[data-reviews-pagination]');
        this.loadingMessage = this.querySelector('[data-reviews-loading]');
        this.appContainer = this.querySelector('.pc-product-reviews__app');

        this.observer = new MutationObserver(() => this.queueRefresh());
        if (this.appContainer) {
          this.observer.observe(this.appContainer, { childList: true, subtree: true });
        }
        this.queueRefresh();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        window.clearTimeout(this.refreshTimer);
      }

      queueRefresh() {
        window.clearTimeout(this.refreshTimer);
        this.refreshTimer = window.setTimeout(() => this.prepareReviews(), 120);
      }

      get reviewStatus() {
        return this.querySelector('.kl_reviews__list_container > [role="status"]');
      }

      get reviewItems() {
        const status = this.reviewStatus;
        if (!status) return [];

        return Array.from(status.children).filter(
          (item) => item.matches('.kl_reviews__review_item') || item.querySelector('.kl_reviews__review_item')
        );
      }

      get loadMoreButton() {
        const dedicatedButton = this.querySelector('.kl_reviews__load_more_button');
        if (dedicatedButton) dedicatedButton.classList.add('pc-review-load-more');
        if (dedicatedButton && !dedicatedButton.disabled) return dedicatedButton;

        const fallbackButton =
          Array.from(this.querySelectorAll('.kl_reviews__button')).find((button) => {
            const label = button.textContent.trim().toLowerCase();
            return !button.disabled && /(mehr|weitere|more|show)/.test(label);
          }) || null;

        if (fallbackButton) fallbackButton.classList.add('pc-review-load-more');
        return fallbackButton;
      }

      async prepareReviews() {
        if (!this.reviewItems.length) return;

        if (this.dataset.loadAll === 'true' && this.loadMoreButton && !this.loadingAll) {
          await this.loadEveryReview();
          return;
        }

        this.renderPagination();
      }

      async loadEveryReview() {
        this.loadingAll = true;
        this.classList.add('is-loading-all');
        this.loadingMessage.hidden = false;

        let attempts = 0;
        while (this.loadMoreButton && attempts < 30) {
          const previousCount = this.reviewItems.length;
          const loadButton = this.loadMoreButton;
          loadButton.dataset.pcReviewLoader = 'true';
          loadButton.click();
          const didLoad = await this.waitForMoreReviews(previousCount);
          attempts += 1;
          if (!didLoad) break;
        }

        this.loadingAll = false;
        this.classList.remove('is-loading-all');
        this.loadingMessage.hidden = true;
        this.renderPagination();
      }

      waitForMoreReviews(previousCount) {
        return new Promise((resolve) => {
          let finished = false;
          const finish = (result) => {
            if (finished) return;
            finished = true;
            observer.disconnect();
            window.clearTimeout(timeout);
            resolve(result);
          };

          const observer = new MutationObserver(() => {
            if (this.reviewItems.length > previousCount) finish(true);
            else if (!this.loadMoreButton) finish(false);
          });

          observer.observe(this, { childList: true, subtree: true });
          const timeout = window.setTimeout(() => finish(this.reviewItems.length > previousCount), 5000);
        });
      }

      getPageSequence(pageCount) {
        if (pageCount <= 10) return Array.from({ length: pageCount }, (_, index) => index + 1);

        const pages = new Set([1, pageCount, this.currentPage - 1, this.currentPage, this.currentPage + 1]);
        const sorted = Array.from(pages)
          .filter((page) => page > 0 && page <= pageCount)
          .sort((a, b) => a - b);
        const sequence = [];

        sorted.forEach((page, index) => {
          if (index > 0 && page - sorted[index - 1] > 1) sequence.push('ellipsis');
          sequence.push(page);
        });

        return sequence;
      }

      renderPagination() {
        const items = this.reviewItems;
        const perPage = Math.max(1, Number.parseInt(this.dataset.perPage || '5', 10));
        const pageCount = Math.max(1, Math.ceil(items.length / perPage));
        this.currentPage = Math.min(this.currentPage, pageCount);

        items.forEach((item, index) => {
          const itemPage = Math.floor(index / perPage) + 1;
          item.classList.toggle('pc-review-page-hidden', itemPage !== this.currentPage);
        });

        this.pagination.replaceChildren();
        if (pageCount <= 1) {
          this.pagination.hidden = true;
          return;
        }

        this.getPageSequence(pageCount).forEach((page) => {
          if (page === 'ellipsis') {
            const ellipsis = document.createElement('span');
            ellipsis.className = 'pc-product-reviews__pagination-ellipsis';
            ellipsis.textContent = '…';
            this.pagination.appendChild(ellipsis);
            return;
          }

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pc-product-reviews__pagination-button';
          button.textContent = page;
          button.setAttribute('aria-label', `Bewertungsseite ${page}`);

          if (page === this.currentPage) {
            button.classList.add('is-active');
            button.setAttribute('aria-current', 'page');
          }

          button.addEventListener('click', () => {
            this.currentPage = page;
            this.renderPagination();
            const list = this.querySelector('.kl_reviews__reviews_list');
            if (list) {
              list.scrollIntoView({
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                block: 'start',
              });
            }
          });

          this.pagination.appendChild(button);
        });

        this.pagination.hidden = false;
      }
    }
  );
}
